// Hooks das promocoes automaticas: config por loja, historico de execucoes,
// ultimos envios e uso do dia. Leitura via RLS (.from), escrita via RPC
// SECURITY DEFINER (save_auto_promotion_config).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  normalizeAutoPromotionConfig,
  serializeAutoPromotionConfig,
  type AutoPromotionConfig,
  type AutoPromotionProvider,
} from "@lucralink/shared";
import { supabase } from "@/lib/supabase";

export interface AutoPromotionState {
  readonly config: AutoPromotionConfig;
  readonly lastRunAt: string | null;
  readonly nextRunAt: string | null;
  readonly lastError: string | null;
  readonly lastErrorAt: string | null;
}

export interface AutoPromotionRun {
  readonly id: string;
  readonly started_at: string;
  readonly status: "sent" | "partial" | "empty" | "skipped" | "error";
  readonly category_id: string | null;
  readonly sent_count: number;
  readonly message: string | null;
}

export interface AutoPromotionSentItem {
  readonly item_id: string;
  readonly title: string | null;
  readonly price_cents: number | null;
  readonly image_url: string | null;
  readonly link_url: string | null;
  readonly sent_at: string;
}

export interface AutoPromotionUsage {
  readonly dailyQuota: number | null;
  readonly sentToday: number;
}

const keys = {
  config: (provider: AutoPromotionProvider) => ["auto-promotion-config", provider] as const,
  runs: (provider: AutoPromotionProvider) => ["auto-promotion-runs", provider] as const,
  sent: (provider: AutoPromotionProvider) => ["auto-promotion-sent", provider] as const,
  usage: ["auto-promotion-usage"] as const,
};

const RECENT_LIMIT = 8;

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

async function fetchState(provider: AutoPromotionProvider): Promise<AutoPromotionState> {
  const { data, error } = await supabase
    .from("auto_promotion_configs")
    .select("*")
    .eq("provider", provider)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  const row = (data ?? null) as Record<string, unknown> | null;
  return {
    config: normalizeAutoPromotionConfig(provider, row),
    lastRunAt: asString(row?.["last_run_at"]),
    nextRunAt: asString(row?.["next_run_at"]),
    lastError: asString(row?.["last_error"]),
    lastErrorAt: asString(row?.["last_error_at"]),
  };
}

export function useAutoPromotionState(provider: AutoPromotionProvider) {
  return useQuery<AutoPromotionState, Error>({
    queryKey: keys.config(provider),
    queryFn: () => fetchState(provider),
    staleTime: 15_000,
  });
}

export function useAutoPromotionRuns(provider: AutoPromotionProvider) {
  return useQuery<AutoPromotionRun[], Error>({
    queryKey: keys.runs(provider),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("auto_promotion_runs")
        .select("id, started_at, status, category_id, sent_count, message")
        .eq("provider", provider)
        .order("started_at", { ascending: false })
        .limit(RECENT_LIMIT);
      if (error) throw error;
      return (data ?? []) as AutoPromotionRun[];
    },
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export function useAutoPromotionSent(provider: AutoPromotionProvider) {
  return useQuery<AutoPromotionSentItem[], Error>({
    queryKey: keys.sent(provider),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("auto_promotion_sent")
        .select("item_id, title, price_cents, image_url, link_url, sent_at")
        .eq("provider", provider)
        .order("sent_at", { ascending: false })
        .limit(RECENT_LIMIT);
      if (error) throw error;
      return (data ?? []) as AutoPromotionSentItem[];
    },
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export function useAutoPromotionUsage() {
  return useQuery<AutoPromotionUsage, Error>({
    queryKey: keys.usage,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_auto_promotion_usage");
      if (error) throw error;
      const row = (data ?? {}) as Record<string, unknown>;
      return {
        dailyQuota: typeof row["daily_quota"] === "number" ? row["daily_quota"] : null,
        sentToday: typeof row["sent_today"] === "number" ? row["sent_today"] : 0,
      };
    },
    staleTime: 30_000,
  });
}

export function useSaveAutoPromotionConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: AutoPromotionConfig) => {
      const { error } = await supabase.rpc("save_auto_promotion_config", {
        p_provider: config.provider,
        p_config: serializeAutoPromotionConfig(config),
      });
      if (error) throw error;
    },
    onSuccess: async (_data, config) => {
      await qc.invalidateQueries({ queryKey: keys.config(config.provider) });
    },
  });
}
